import React from 'react';

import { classnames, padding } from '@lumapps/classnames';
import { mdiCalendar } from '@lumapps/lumx/icons';
import { DatePickerField, DatePickerFieldProps } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { useObjectFilter, UseObjectOptions, UseObjectFilter } from '../../hooks/useObjectFilter';
import { Filter, type FilterProps } from '../Filter';

export type DateFilterValues = {
    from?: Date;
    to?: Date;
};

export interface DateFilterProps extends FilterProps {
    /** Language of the site */
    language: string;
    /** Date from picker field additional props */
    fromProps?: Partial<DatePickerFieldProps>;
    /** Date to picker field additional props */
    toProps?: Partial<DatePickerFieldProps>;
    /** default values */
    defaults?: UseObjectOptions<DateFilterValues>;
    /**  USED INTERNALLY, DO NOT USE, INJECTED BY PARENT `Filters` */
    onSelected?: UseObjectFilter<DateFilterValues>['onSelected'];
    /**  USED INTERNALLY, DO NOT USE, INJECTED BY PARENT `Filters` */
    selected?: UseObjectFilter<DateFilterValues>['selected'];
    selection?: UseObjectFilter<DateFilterValues>['selection'];
}

/**
 * Filter that displays 2 date pickers in order to filter between dates.
 *
 * @family Filters
 * @param DateFilterProps
 * @returns DateFilter
 */
export const DateFilter: React.FC<DateFilterProps> = ({
    language,
    selection = {},
    onSelected,
    fromProps = {},
    toProps = {},
    ...props
}) => {
    const { translateKey } = useTranslate();

    const onChange = (date: Date | undefined, key: string) => {
        if (date) {
            // date must be set at start of day
            date.setHours(0, 0, 0);
        }

        onSelected?.({
            ...selection,
            [key]: date,
        });
    };

    return (
        <Filter {...props} isFieldset titleAs="heading">
            <DatePickerField
                name="from"
                locale={language}
                value={selection?.from}
                onChange={(date) => onChange(date, 'from')}
                clearButtonProps={{ label: translateKey(GLOBAL.CLEAR) }}
                nextButtonProps={{ label: translateKey(GLOBAL.NEXT_MONTH) }}
                previousButtonProps={{ label: translateKey(GLOBAL.PREVIOUS_MONTH) }}
                label={translateKey(GLOBAL.DATE_FROM)}
                icon={mdiCalendar}
                maxDate={selection?.to}
                {...toProps}
                className={classnames(toProps.className, padding('top', null))}
            />
            <DatePickerField
                name="to"
                locale={language}
                value={selection?.to}
                onChange={(date) => onChange(date, 'to')}
                clearButtonProps={{ label: translateKey(GLOBAL.CLEAR) }}
                nextButtonProps={{ label: translateKey(GLOBAL.NEXT_MONTH) }}
                previousButtonProps={{ label: translateKey(GLOBAL.PREVIOUS_MONTH) }}
                label={translateKey(GLOBAL.DATE_TO)}
                icon={mdiCalendar}
                minDate={selection?.from}
                {...fromProps}
            />
        </Filter>
    );
};

Object.assign(DateFilter, { hook: useObjectFilter });
